import { ApiError } from '../../api/client';
import type { StudioCommand } from '../../api/speech';
/** Экраны с блоком «Съёмка»: их `shoot` — объект строк блока (03-api §9.2) */
const SHOOT_SCREENS = ['photo', 'video', 'shoot', 'pshoot'];
const photoKinds = new Set(['camera', 'lens', 'aperture', 'light', 'color', 'angle']);
export function validShoot(value: unknown, screen = 'photo'): value is Record<string, string> {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.entries(value).length <= (screen === 'video' ? 8 : 6) &&
    Object.entries(value).every(
      ([kind, id]) =>
        (photoKinds.has(kind) || (screen === 'video' && ['move', 'tempo'].includes(kind))) &&
        typeof id === 'string' &&
        id.length > 0 &&
        id.length <= 128,
    )
  );
}
/** Строковые типы контракта (docs/59 §1): их значение и в JSON — обычная строка, разбирать её не нужно. */
const STRING_TYPES = new Set(['string', 'enum', 'id', 'upload', 'text']);
/** Поля с JSON-значением (docs/59-studio-backend-contracts.md §1): `value_encoding: "json"` у полей шаблона и контракта экрана
    и нестроковый тип (число, логическое, массив, объект) — их строку формы клиент разбирает перед отправкой. */
export function studioJsonKeys(form: { fields?: Array<{ key: string }>; screen_fields?: Array<{ key: string }> } | null | undefined): string[] {
  const all = [...(form?.fields ?? []), ...(Array.isArray(form?.screen_fields) ? form!.screen_fields : [])] as Array<{
    key: string;
    type?: string;
    value_encoding?: string;
  }>;
  return [...new Set(all.filter(field => field?.value_encoding === 'json' && !STRING_TYPES.has(field.type ?? '')).map(field => field.key))];
}
/** JSON-поля с перечислимым строковым значением (`enum`, `id`): транспорт шлёт их строкой как есть, но формы набора
    «Фотосессий» (shoot/kit encode) хранят их JSON-текстом — '"neuro"'. Такую строку-литерал надо развернуть, иначе
    сервер получает значение с кавычками: 422 input_field_invalid (shoot_id «Фотосессии из фото» на стенде). Свободный
    текст (`string`, `text`) не трогаем: кавычки в нём могут быть словами человека. */
export function studioJsonEnumKeys(form: { fields?: Array<{ key: string }>; screen_fields?: Array<{ key: string }> } | null | undefined): string[] {
  const all = [...(form?.fields ?? []), ...(Array.isArray(form?.screen_fields) ? form!.screen_fields : [])] as Array<{
    key: string;
    type?: string;
    value_encoding?: string;
  }>;
  return [...new Set(all.filter(field => field?.value_encoding === 'json' && (field.type === 'enum' || field.type === 'id')).map(field => field.key))];
}
const JSON_MAX = 64 * 1024;
/** JSON-значение поля: числа, строки, логические, массивы и объекты ограниченной глубины и размера. */
export function validJsonValue(value: unknown, depth = 0): boolean {
  if (depth > 6) return false;
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.length <= 500 && value.every(item => validJsonValue(item, depth + 1));
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    return entries.length <= 200 && entries.every(([key, item]) => key.length <= 128 && validJsonValue(item, depth + 1));
  }
  return false;
}
function jsonSized(value: unknown) {
  try {
    return JSON.stringify(value).length <= JSON_MAX;
  } catch {
    return false;
  }
}
/** UI хранит JSON строкой, но transport и durable intent фиксируют объект до первого POST.
    jsonKeys — поля формы с `value_encoding: "json"`: их строка разбирается для любого экрана. */
export function studioInputs(screen: string, inputs: Record<string, string>, jsonKeys: string[] = [], enumKeys: string[] = []): StudioCommand['inputs'] {
  const result: Record<string, unknown> = { ...inputs };
  for (const key of enumKeys) {
    const raw = inputs[key];
    if (typeof raw !== 'string' || !raw.startsWith('"')) continue;
    try {
      const value = JSON.parse(raw) as unknown;
      if (typeof value === 'string') result[key] = value;
    } catch {
      /* не литерал JSON — значение остаётся как есть, его проверит сервер */
    }
  }
  for (const key of jsonKeys) {
    if (key === 'shoot' && SHOOT_SCREENS.includes(screen)) continue;
    const raw = inputs[key];
    if (raw === undefined || raw === '') {
      delete result[key];
      continue;
    }
    let value: unknown;
    try {
      value = JSON.parse(raw);
    } catch {
      throw new ApiError(422, 'validation_error', '');
    }
    if (!validJsonValue(value) || !jsonSized(value)) throw new ApiError(422, 'validation_error', '');
    result[key] = value;
  }
  if (SHOOT_SCREENS.includes(screen) && inputs.shoot !== undefined) {
    let shoot: unknown;
    try {
      shoot = JSON.parse(inputs.shoot);
    } catch {
      throw new ApiError(422, 'shoot_invalid', '');
    }
    if (!validShoot(shoot, screen)) throw new ApiError(422, 'shoot_invalid', '');
    result.shoot = shoot;
  }
  return result as StudioCommand['inputs'];
}
/** Никакие другие вложенные пользовательские JSON-поля не восстанавливаются как команда: только `shoot` «Фото» и «Видео»
    и поля, которые форма объявила JSON (jsonKeys сохраняются в намерении вместе с телом). */
export function validStudioInputs(screen: string, inputs: unknown, jsonKeys: string[] = []): boolean {
  return (
    !!inputs &&
    typeof inputs === 'object' &&
    !Array.isArray(inputs) &&
    jsonSized(inputs) &&
    Object.entries(inputs).every(
      ([kind, value]) =>
        typeof value === 'string' ||
        (SHOOT_SCREENS.includes(screen) && kind === 'shoot' && validShoot(value, screen)) ||
        (jsonKeys.includes(kind) && !(SHOOT_SCREENS.includes(screen) && kind === 'shoot') && validJsonValue(value)),
    )
  );
}
